import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Heading, Paragraph, Tag } from '@digdir/designsystemet-react';
import { PlusIcon, TrashIcon } from '@navikt/aksel-icons';
import type { AssessedArea, DataSource } from '../../../api/workEnvironmentTypes';
import {
  useOpenProposalsMutation,
  useRemoveDataSourceMutation,
} from '../../../hooks/mutations/useWorkEnvironmentAssessmentMutations';
import { areasByLevel, dataLevelLabel, sourcesFor } from '../dataSourcesView';
import { areaName, assessedLevel, assessedPlacement, LEVEL_COLORS } from '../riskView';
import { dayAndMonth } from '../surveyProcessView';
import { useSurveyActions } from '../../../hooks/useSurveyActions';
import { DataSourceDialog } from './DataSourceDialog';
import process from '../WorkEnvironmentSurveyProcess.module.css';
import classes from '../WorkEnvironmentSurveyRisk.module.css';

type Props = { surveyId: number; areas: AssessedArea[]; sources: DataSource[] };

/** Step 4: safety inspections, sick leave and other data give a fuller picture. The step is optional. */
export const DataSourcesCard = ({ surveyId, areas, sources }: Props): ReactElement => {
  const { t, i18n } = useTranslation();
  const { may } = useSurveyActions();
  const [adding, setAdding] = useState<AssessedArea | null>(null);
  const remove = useRemoveDataSourceMutation(surveyId);
  const open = useOpenProposalsMutation(surveyId);
  const levelName = (level: string) => t(`workEnvironmentRisk.matrix.levels.${level}`);

  return (
    <section aria-labelledby="data-heading" className={process.card}>
      <div className={process.cardBody}>
        <div className={process.sideHeading}>
          <span className={`ds-label ${process.eyebrow}`} data-size="sm">
            {t('workEnvironmentRisk.data.eyebrow')}
          </span>
          <Heading level={2} data-size="sm" id="data-heading">
            {t('workEnvironmentRisk.data.heading')}
          </Heading>
          <Paragraph data-size="sm" className={process.subtle}>
            {t('workEnvironmentRisk.data.intro')}
          </Paragraph>
        </div>
        {remove.isError && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentRisk.data.removeFailed')}</Paragraph>
          </Alert>
        )}
        {areasByLevel(areas).map(({ level, areas: group }) => (
          <div key={level} className={classes.areaBody}>
            <Heading level={3} data-size="2xs">
              {t('workEnvironmentRisk.data.group', { level: levelName(level), count: group.length })}
            </Heading>
            <ul className={classes.dataAreas}>
              {group.map((area) => {
                const own = sourcesFor(area.key, sources);
                return (
                  <li key={area.key} className={classes.dataArea}>
                    <div className={classes.areaSummary}>
                      <span className={classes.areaTitle}>
                        <span className="ds-label">{areaName(area, t)}</span>
                        <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
                          {t('workEnvironmentRisk.data.areaMeta', {
                            ...assessedPlacement(area),
                            count: own.length,
                          })}
                        </span>
                      </span>
                      <Tag data-size="sm" data-color={LEVEL_COLORS[assessedLevel(area)]}>
                        {levelName(assessedLevel(area))}
                      </Tag>
                    </div>
                    {own.length > 0 && (
                      <ul className={classes.sources}>
                        {own.map((source) => (
                          <li key={source.id} className={classes.source}>
                            <span className={classes.areaTitle}>
                              <span className="ds-label" data-size="sm">
                                {source.title}
                              </span>
                              <span className={`ds-paragraph ${process.subtle}`} data-size="xs">
                                {t(`workEnvironmentRisk.data.types.${source.type}`)} ·{' '}
                                {dayAndMonth(source.date, i18n.language)}
                              </span>
                              <span className="ds-paragraph" data-size="sm">
                                {source.summary}
                              </span>
                            </span>
                            <span className={classes.areaTags}>
                              <Tag data-size="sm" data-color={source.dimension === 'LOAD' ? 'warning' : 'info'}>
                                {dataLevelLabel(source, t)}
                              </Tag>
                              {may('addData') && (
                                <Button
                                  variant="tertiary"
                                  data-size="sm"
                                  icon
                                  aria-label={t('workEnvironmentRisk.data.remove', { title: source.title })}
                                  loading={remove.isPending && remove.variables === source.id}
                                  onClick={() => remove.mutate(source.id)}
                                >
                                  <TrashIcon aria-hidden />
                                </Button>
                              )}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {may('addData') && (
                      <div>
                        <Button variant="secondary" data-size="sm" onClick={() => setAdding(area)}>
                          <PlusIcon aria-hidden />
                          {t('workEnvironmentRisk.data.add')}
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      {may('advance') && (
      <div className={process.cardFooter}>
        <Paragraph data-size="sm">
          {sources.length > 0 ? t('workEnvironmentRisk.data.moveOn') : t('workEnvironmentRisk.data.moveOnWithout')}
        </Paragraph>
        {open.isError && (
          <Alert data-color="danger">
            <Paragraph>{t('workEnvironmentRisk.data.moveOnFailed')}</Paragraph>
          </Alert>
        )}
        <Button variant="primary" data-size="sm" loading={open.isPending} onClick={() => open.mutate(undefined)}>
          {t(sources.length > 0 ? 'workEnvironmentRisk.data.continue' : 'workEnvironmentRisk.data.continueWithout')}
        </Button>
      </div>
      )}
      {adding && <DataSourceDialog surveyId={surveyId} area={adding} onClose={() => setAdding(null)} />}
    </section>
  );
};
